// Hook for managing customer-related actions during invoice creation

import { useState, useCallback, useRef, useEffect } from 'react';
import { Buyer } from '../../types/invoiceTypes';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebaseConfig'; 
import { collection, getDocs, where, query } from 'firebase/firestore'; // Nur Lesezugriff für Duplikatprüfung
import logger, { nativeConsole } from '../../utils/logger';
import { deepEqual } from '../../utils/objectUtils';

// **FIX 6: Dialog-Manager für Race Condition Prevention**
interface DialogManagerState {
  isActive: boolean;
  type: 'customer' | 'product' | null;
  priority: number;
}

// **FIX 7: Global Dialog Manager (singleton pattern)**
let globalDialogManager: DialogManagerState = {
  isActive: false,
  type: null,
  priority: 0
};

const acquireDialogLock = (type: 'customer' | 'product', priority: number = 1): boolean => {
  if (!globalDialogManager.isActive || globalDialogManager.priority < priority) {
    globalDialogManager = { isActive: true, type, priority };
    return true;
  }
  return false;
};

const releaseDialogLock = (type: 'customer' | 'product'): void => {
  if (globalDialogManager.type === type) {
    globalDialogManager = { isActive: false, type: null, priority: 0 };
  }
};

// Exported type for the hook's return value, including the customer metadata
export interface CustomerManagementResult {
  customerMeta: { 
    action: 'create' | 'update' | 'none'; 
    customerId: string | null; 
  };
  checkForUpdate: (currentBuyer: Buyer) => Promise<boolean | 'continue'>;
  handleCustomerSelection: (selectedCustomer: Buyer, selectedCustomerId: string) => void;
  // Add DB operation functions and state
  updateSelectedCustomer: (currentBuyerData: Buyer) => Promise<boolean>;
  createCustomer: (newBuyerData: Buyer) => Promise<string | null>; // Returns new customer ID or null on error
  isLoading: boolean;
  error: string | null;
  // Dialog-related properties
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  dialogConfig: CustomerDialogConfig;
  handleDialogAction: (action: string) => Promise<'continue' | 'abort'>;
  // **FIX: Customer meta lock functions**
  releaseCustomerMetaLock: () => void;
  customerMetaLocked: boolean;
  // **FIX: Funktion zum Abrufen des aktuellen customerMeta**
  getCurrentCustomerMeta: () => { action: 'create' | 'update' | 'none'; customerId: string | null };
}

// Helper function based on logic from BuyerDetailsForm
const compareCustomerData = (
    currentBuyer: Buyer,
    originalBuyer: Buyer | null,
): { needsUpdate: boolean; needsCreate: boolean } => {
    if (!originalBuyer || !originalBuyer.name) {
        // If no original or original is invalid, assume creation if current buyer has data
        return { needsUpdate: false, needsCreate: !!currentBuyer.name };
    }

    // Check if identifying fields (companyName or personName) have changed
    // Treat a change here as needing a *new* customer, not an update.
    const identifierChanged = 
        (currentBuyer.companyName || null) !== (originalBuyer.companyName || null) ||
        (currentBuyer.personName || null) !== (originalBuyer.personName || null);

    if (identifierChanged) {
        nativeConsole.log("Customer identifier changed - suggesting create new.");
        // Only suggest create if the *current* buyer has a name
        return { needsUpdate: false, needsCreate: !!(currentBuyer.companyName || currentBuyer.personName) }; 
    }

    // Check if any *other* relevant field has changed for an update
    // Use deepEqual for potentially complex fields or simple comparison for others
    const hasChanges = 
        currentBuyer.street !== originalBuyer.street ||
        (currentBuyer.street2 || null) !== (originalBuyer.street2 || null) ||
        currentBuyer.zip !== originalBuyer.zip ||
        currentBuyer.city !== originalBuyer.city ||
        currentBuyer.country !== originalBuyer.country ||
        (currentBuyer.email || null) !== (originalBuyer.email || null) ||
        (currentBuyer.phone || null) !== (originalBuyer.phone || null) ||
        (currentBuyer.website || null) !== (originalBuyer.website || null) ||
        (currentBuyer.reference || null) !== (originalBuyer.reference || null) ||
        (currentBuyer.id || null) !== (originalBuyer.id || null) || // Compare user-defined ID
        (currentBuyer.customerId || null) !== (originalBuyer.customerId || null); // Compare system customerId
        // Add comparison for communication if needed: !deepEqual(currentBuyer.communication, originalBuyer.communication)

    if (hasChanges) {
        nativeConsole.log("Customer data changed - suggesting update.");
        return { needsUpdate: true, needsCreate: false };
    }

    nativeConsole.log("No significant customer data changes detected.");
    return { needsUpdate: false, needsCreate: false };
};

// Korrigiere den DialogConfig Typ, um die fehlenden Felder zu enthalten
interface CustomerDialogConfig {
  title: string;
  message: string;
  action: 'create' | 'update' | 'none';
  
  // Ersetze 'data' und 'original' mit unseren neuen Feldnamen
  data: Buyer; // Für Kompatibilität mit älterem Code
  original: Buyer | null; // Für Kompatibilität mit älterem Code
  
  // Neue Feldnamen für klarere Semantik
  buyer?: Buyer;
  customerId?: string | null;
  originalCustomer?: Buyer | null;
  
  // **FIX: Füge options hinzu für ConfirmationDialog**
  options?: Array<{
    label: string;
    action: () => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger';
  }>;
}

export const useCustomerManagement = (): CustomerManagementResult => {
  const { currentUser, effectiveScopeUid } = useAuth();

  // **FIX 8: Verbesserte State-Verwaltung mit Refs für Race Condition Prevention**
  const [customerMeta, setCustomerMeta] = useState<{ action: 'create' | 'update' | 'none', customerId: string | null }>({ action: 'none', customerId: null });
  const [originalBuyer, setOriginalBuyer] = useState<Buyer | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // **FIX: Verhindere unbeabsichtigtes Zurücksetzen der customerMeta**
  const [customerMetaLocked, setCustomerMetaLocked] = useState(false);
  const customerMetaLockRef = useRef<boolean>(false);
  // **FIX: Zusätzliche Ref für sofortigen Zugriff auf customerMeta**
  const customerMetaRef = useRef<{ action: 'create' | 'update' | 'none', customerId: string | null }>({ action: 'none', customerId: null });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogConfig, setDialogConfig] = useState<CustomerDialogConfig>({
    title: '',
    message: '',
    action: 'none',
    data: {} as Buyer,
    original: null,
    options: []
  });

  // **FIX 9: Ref für sichere Async-Operationen**
  const isMountedRef = useRef(true);
  const dialogLockRef = useRef<boolean>(false);

  // **FIX 10: Verbessertes Mounting/Cleanup-Management**
  useEffect(() => {
    // Setze beim Mount/Re-Mount auf true
    isMountedRef.current = true;
    // **FIX: Initialisiere customerMetaRef mit aktuellem customerMeta-Wert**
    customerMetaRef.current = customerMeta;
    
    return () => {
      // Cleanup beim Unmount
      isMountedRef.current = false;
      if (dialogLockRef.current) {
        releaseDialogLock('customer');
        dialogLockRef.current = false;
      }
    };
  }, [customerMeta]);

  // **FIX: Sichere setCustomerMeta Funktion mit Lock-Schutz**
  const setCustomerMetaSafe = useCallback((newMeta: { action: 'create' | 'update' | 'none', customerId: string | null }, force: boolean = false) => {
    if (!force && customerMetaLockRef.current) {
      nativeConsole.log('customerMeta update blocked by lock:', newMeta);
      nativeConsole.log('Current customerMeta lock state:', customerMetaLockRef.current);
      return;
    }
    
    nativeConsole.log('Setting customerMeta:', newMeta);
    nativeConsole.log('Force flag:', force);
    nativeConsole.log('Lock state before setting:', customerMetaLockRef.current);
    
    // **FIX: Setze sowohl State als auch Ref für sofortigen Zugriff**
    setCustomerMeta(newMeta);
    customerMetaRef.current = newMeta;
    
    // Wenn eine Aktion gesetzt wird, aktiviere das Lock
    if (newMeta.action !== 'none') {
      nativeConsole.log('Activating customerMeta lock');
      customerMetaLockRef.current = true;
      setCustomerMetaLocked(true);
    }
  }, []); // **FIX: Entferne customerMeta Dependency um Race Conditions zu vermeiden**
  
  // **FIX: Funktion zum Freigeben des customerMeta Locks**
  const releaseCustomerMetaLock = useCallback(() => {
    nativeConsole.log('Releasing customerMeta lock');
    customerMetaLockRef.current = false;
    setCustomerMetaLocked(false);
    // Nach Lock-Freigabe auf 'none' zurücksetzen
    const resetMeta = { action: 'none' as const, customerId: null };
    setCustomerMeta(resetMeta);
    customerMetaRef.current = resetMeta;
  }, []);

  // **FIX: Funktion zum Abrufen des aktuellen customerMeta aus der Ref**
  const getCurrentCustomerMeta = useCallback(() => {
    nativeConsole.log('getCurrentCustomerMeta called - returning:', customerMetaRef.current);
    return customerMetaRef.current;
  }, []);

  // **FIX 11: Sichere Dialog-Öffnung mit Lock-Management**
  const openDialog = useCallback((config: CustomerDialogConfig): boolean => {
    if (!isMountedRef.current) {
      return false;
    }

    nativeConsole.log('=== openDialog START ===');
    nativeConsole.log('config:', config);
    nativeConsole.log('config.options:', config.options);

    // Versuche Dialog-Lock zu erwerben
    if (!acquireDialogLock('customer', 1)) {
      nativeConsole.warn('Customer dialog blocked - another dialog is active');
      return false;
    }

    dialogLockRef.current = true;
    setDialogConfig(config);
    setDialogOpen(true);
    nativeConsole.log('Customer dialog opened successfully');
    nativeConsole.log('Final dialogConfig set:', config);
    return true;
  }, []);

  // **FIX 12: Sichere Dialog-Schließung OHNE Lock-Freigabe**
  const closeDialog = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }

    setDialogOpen(false);
    if (dialogLockRef.current) {
      releaseDialogLock('customer');
      dialogLockRef.current = false;
    }
    // **IMPORTANT: customerMeta Lock wird NICHT hier freigegeben!**
    // Das Lock muss bis nach der Rechnungsstellung bestehen bleiben
    nativeConsole.log('Customer dialog closed - customerMeta lock remains active');
  }, []);

  // Function called when a customer is selected from autocomplete
  const handleCustomerSelection = useCallback((selectedCustomer: Buyer, customerDocId: string) => {
    if (!isMountedRef.current) return;
    
    nativeConsole.log(`Customer selected in hook: ID=${customerDocId}`, selectedCustomer);
    nativeConsole.log('handleCustomerSelection: Current lock state:', customerMetaLockRef.current);
    
    // Store the original data and the selected Firestore document ID
    setOriginalBuyer(JSON.parse(JSON.stringify(selectedCustomer))); // Deep copy
    setSelectedCustomerId(customerDocId);
    
    // **FIX: Nur zurücksetzen wenn kein Lock aktiv ist**
    if (!customerMetaLockRef.current) {
      nativeConsole.log('handleCustomerSelection: Resetting customerMeta (no lock)');
      setCustomerMetaSafe({ action: 'none', customerId: customerDocId }, false); // Don't force, respect lock
    } else {
      nativeConsole.log('handleCustomerSelection: Skipping customerMeta reset (lock active)');
    }
    setError(null); // Clear error on new selection
  }, []); // **FIX: Remove dependency to avoid recreating function**

  // --- Database Operations with improved error handling ---

  // Function to mark customer for update (actual DB operation happens in backend)
  const updateSelectedCustomer = useCallback(async (currentBuyerData: Buyer): Promise<boolean> => {
    if (!isMountedRef.current) return false;
    if (!currentUser) { setError("Benutzer nicht angemeldet."); return false; }
    if (!selectedCustomerId) { setError("Kein Kunde zum Aktualisieren ausgewählt."); return false; }

    // **FIX: Keine direkte Firestore-Speicherung mehr im Frontend!
    // Das Backend übernimmt die Speicherung - wir setzen nur die Absicht (action: 'update')**
    nativeConsole.log(`Kunde mit ID ${selectedCustomerId} wird für Update markiert (Backend übernimmt Speicherung)`);
    setOriginalBuyer(JSON.parse(JSON.stringify(currentBuyerData)));
    // Setze action auf 'update' - das Backend wird die eigentliche Speicherung durchführen
    setCustomerMetaSafe({ action: 'update', customerId: selectedCustomerId }, true);
    return true;
  }, [currentUser, selectedCustomerId, setCustomerMetaSafe]);

  // Function to mark customer for creation (actual DB operation happens in backend)
  const createCustomer = useCallback(async (newBuyerData: Buyer): Promise<string | null> => {
    if (!isMountedRef.current) return null;
    if (!currentUser) { setError("Benutzer nicht angemeldet."); return null; }
    
    // **FIX: Keine direkte Firestore-Speicherung mehr im Frontend!
    // Das Backend übernimmt die Speicherung - wir setzen nur die Absicht (action: 'create')**
    nativeConsole.log("Neuer Kunde wird für Erstellung markiert (Backend übernimmt Speicherung)");
    setOriginalBuyer(JSON.parse(JSON.stringify(newBuyerData)));
    // Setze action auf 'create' - das Backend wird die eigentliche Speicherung durchführen
    // customerId ist null, da der Kunde noch nicht existiert
    setCustomerMetaSafe({ action: 'create', customerId: null }, true);
    return 'pending-backend-creation'; // Marker dass Backend erstellen soll
  }, [currentUser, setCustomerMetaSafe]);

  // **FIX 13: Verbesserter Dialog-Handler mit Lock-Management**
  const handleDialogAction = useCallback(async (action: string): Promise<'continue' | 'abort'> => {
    if (!isMountedRef.current) return 'abort';
    
    nativeConsole.log('=== handleDialogAction START ===');
    nativeConsole.log('action:', action);
    nativeConsole.log('isMountedRef.current:', isMountedRef.current);
    nativeConsole.log('dialogOpen:', dialogOpen);
    
    // Wenn 'none' gewählt wird, schließe den Dialog und setze den Prozess fort
    if (action === 'none') {
      nativeConsole.log("Kundendialog abgebrochen, setze Prozess fort");
      closeDialog();
      return 'continue'; // Prozess fortsetzen
    }

    if (action === 'update') {
      nativeConsole.log("Markiere Kunde für Update (Backend übernimmt Speicherung)...");
      const success = await updateSelectedCustomer(dialogConfig.data);
      nativeConsole.log("Update marking result:", success);
      if (success && isMountedRef.current) {
        nativeConsole.log("Kunde für Update markiert - customerMeta.action = 'update', Backend wird speichern");
      }
      closeDialog();
      return 'continue';
    } else if (action === 'create') {
      nativeConsole.log("Markiere neuen Kunden für Erstellung (Backend übernimmt Speicherung)...");
      const result = await createCustomer(dialogConfig.data);
      nativeConsole.log("Create marking result:", result);
      if (result && isMountedRef.current) {
        nativeConsole.log("Neuer Kunde für Erstellung markiert - customerMeta.action = 'create', Backend wird speichern");
      }
      closeDialog();
      return 'continue';
    }
    
    // Standardfall
    nativeConsole.log("Unbekannte Aktion, schließe Dialog");
    closeDialog();
    return 'abort'; // Prozess abbrechen
  }, [dialogConfig, updateSelectedCustomer, createCustomer, closeDialog, dialogOpen, selectedCustomerId]);

  // **FIX 14: Verbesserte checkForUpdate-Funktion mit Dialog-Management**
  const checkForUpdate = useCallback(async (currentBuyer: Buyer): Promise<boolean | 'continue'> => {
    // **DEBUG: Umfassende Logs für Fehlerbehebung - IMMER AUSFÜHREN**
    nativeConsole.log('=== checkForUpdate START ===');
    nativeConsole.log('currentBuyer:', currentBuyer);
    nativeConsole.log('selectedCustomerId:', selectedCustomerId);
    nativeConsole.log('originalBuyer:', originalBuyer);
    nativeConsole.log('dialogOpen:', dialogOpen);
    nativeConsole.log('globalDialogManager:', globalDialogManager);
    nativeConsole.log('currentUser:', currentUser);
    nativeConsole.log('isMountedRef.current:', isMountedRef.current);
    
    if (!isMountedRef.current) {
      nativeConsole.log('Component not mounted, returning false');
      return false;
    }

    if (!currentUser) {
      nativeConsole.log('No user, returning false');
      return false;
    }

    if (!currentBuyer) {
      nativeConsole.log('No buyer data, returning false');
      return false;
    }

    // **FIX 15: Prüfe Dialog-Status**
    const isDialogCurrentlyOpen = dialogOpen || globalDialogManager.isActive;
    nativeConsole.log('Dialog currently open:', isDialogCurrentlyOpen);
    
    if (isDialogCurrentlyOpen) {
      nativeConsole.log('Dialog already open, returning false');
      return false;
    }

    const isEmptyOrResetForm = !currentBuyer.name && !currentBuyer.companyName && !currentBuyer.personName;
    nativeConsole.log('isEmptyOrResetForm:', isEmptyOrResetForm);
    
    if (isEmptyOrResetForm) {
      nativeConsole.log('Empty form, returning false');
      return false;
    }

    // **FIX 16: Kundendaten-Prüfung**
    const hasOriginalData = !!originalBuyer;
    const currentCustomerName = currentBuyer.name || currentBuyer.companyName || currentBuyer.personName;
    
    nativeConsole.log('Prüfe Kundendaten:', {
      selectedCustomerId,
      hasOriginalData,
      currentCustomerName
    });

    // **CASE 1: Bestehender Kunde ausgewählt**
    if (selectedCustomerId) {
      // Prüfe, ob sich die Daten geändert haben
      if (hasOriginalData && originalBuyer) {
        const hasChanges = (
          originalBuyer.name !== currentBuyer.name ||
          originalBuyer.companyName !== currentBuyer.companyName ||
          originalBuyer.personName !== currentBuyer.personName ||
          originalBuyer.street !== currentBuyer.street ||
          originalBuyer.zip !== currentBuyer.zip ||
          originalBuyer.city !== currentBuyer.city ||
          originalBuyer.country !== currentBuyer.country ||
          originalBuyer.email !== currentBuyer.email ||
          originalBuyer.phone !== currentBuyer.phone
        );
        
        nativeConsole.log('Changes detected for existing customer:', hasChanges);
        
        if (hasChanges) {
          nativeConsole.log('=== SHOWING UPDATE CUSTOMER DIALOG ===');
          
          // Promise erstellen, die erst aufgelöst wird, wenn Dialog geschlossen wird
          return new Promise((resolve) => {
            const dialogConfig = {
              title: 'Kunde aktualisieren',
              message: `Möchten Sie die Änderungen für den Kunden "${currentCustomerName}" speichern?`,
              action: 'update' as const,
              data: currentBuyer,
              original: originalBuyer,
              options: [
                {
                  label: 'Speichern',
                  action: async () => {
                    nativeConsole.log('=== UPDATE CUSTOMER BUTTON CLICKED ===');
                    const result = await handleDialogAction('update');
                    nativeConsole.log('Update action result:', result);
                    resolve(result === 'continue');
                  },
                  variant: 'primary' as const
                },
                {
                  label: 'Abbrechen',
                  action: async () => {
                    nativeConsole.log('=== CANCEL UPDATE CUSTOMER BUTTON CLICKED ===');
                    const result = await handleDialogAction('none');
                    nativeConsole.log('Cancel action result:', result);
                    resolve(result === 'continue');
                  },
                  variant: 'secondary' as const
                }
              ]
            };
            
            openDialog(dialogConfig);
          });
        }
      }
      
      nativeConsole.log('No changes for existing customer, continue');
      return true;
    }

    // **CASE 2: Neuer Kunde (kein selectedCustomerId)**
    nativeConsole.log('=== NEW CUSTOMER CHECK ===');
    
    const hasName = !!(currentBuyer.name || currentBuyer.companyName || currentBuyer.personName);
    const hasCompanyName = !!currentBuyer.companyName;
    const hasPersonName = !!currentBuyer.personName;
    const hasStreet = !!currentBuyer.street;
    const hasCity = !!currentBuyer.city;
    const hasZip = !!currentBuyer.zip;
    
    nativeConsole.log('New customer conditions met:', {
      hasName,
      hasCompanyName,
      hasPersonName,
      hasStreet,
      hasCity,
      hasZip
    });

    // Prüfe, ob es sich um einen neuen Kunden handelt
    if (hasName && hasStreet && hasCity && hasZip) {
      // Prüfe, ob der Kunde bereits existiert
      try {
        const searchName = currentBuyer.name || currentBuyer.companyName || currentBuyer.personName;
        nativeConsole.log('Searching for existing customer with name:', searchName);
        
        const customerQuery = query(
          collection(db, 'customers'),
          where('userId', '==', effectiveScopeUid),
          where('name', '==', searchName)
        );
        
        const querySnapshot = await getDocs(customerQuery);
        nativeConsole.log('Database query result:', {
          isEmpty: querySnapshot.empty,
          size: querySnapshot.size
        });
        
        if (querySnapshot.empty) {
          nativeConsole.log('=== SHOWING NEW CUSTOMER DIALOG ===');
          nativeConsole.log('Dialog data:', currentBuyer);
          
          // Promise erstellen, die erst aufgelöst wird, wenn Dialog geschlossen wird
          return new Promise((resolve) => {
            const dialogConfig = {
              title: 'Neuen Kunden anlegen',
              message: `Möchten Sie den Kunden "${currentCustomerName}" in der Kundendatenbank speichern?`,
              action: 'create' as const,
              data: currentBuyer,
              original: null,
              options: [
                {
                  label: 'Speichern',
                  action: async () => {
                    nativeConsole.log('=== CREATE CUSTOMER BUTTON CLICKED ===');
                    const result = await handleDialogAction('create');
                    nativeConsole.log('Create action result:', result);
                    resolve(result === 'continue');
                  },
                  variant: 'primary' as const
                },
                {
                  label: 'Abbrechen',
                  action: async () => {
                    nativeConsole.log('=== CANCEL CREATE CUSTOMER BUTTON CLICKED ===');
                    const result = await handleDialogAction('none');
                    nativeConsole.log('Cancel action result:', result);
                    resolve(result === 'continue');
                  },
                  variant: 'secondary' as const
                }
              ]
            };
            
            openDialog(dialogConfig);
          });
        } else {
          nativeConsole.log('Customer already exists, continue');
          return true;
        }
      } catch (error) {
        nativeConsole.error('Error checking existing customer:', error);
        return false;
      }
    }

    nativeConsole.log('No customer dialog needed, continue');
    return true;
  }, [currentUser, selectedCustomerId, originalBuyer, dialogOpen, globalDialogManager, openDialog, handleDialogAction]);

  // **FIX 15: Verbesserte setDialogOpen-Funktion**
  const setDialogOpenSafe = useCallback((open: boolean) => {
    if (!isMountedRef.current) return;
    
    if (open) {
      // Verwende openDialog für sichere Öffnung
      nativeConsole.warn("setDialogOpen(true) sollte vermieden werden - verwende openDialog stattdessen");
    } else {
      // Verwende closeDialog für sichere Schließung
      closeDialog();
    }
  }, [closeDialog]);

  return {
    customerMeta,
    checkForUpdate,
    handleCustomerSelection,
    // Expose DB functions and state
    updateSelectedCustomer,
    createCustomer,
    isLoading,
    error,
    // Dialog-related properties
    dialogOpen,
    setDialogOpen: setDialogOpenSafe,
    dialogConfig,
    handleDialogAction,
    // **FIX: Expose customer meta lock functions**
    releaseCustomerMetaLock,
    customerMetaLocked,
    // **FIX: Expose getCurrentCustomerMeta function**
    getCurrentCustomerMeta
  };
}; 